import { SubgraphType } from 'config/subgraph'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import { Transaction, TransactionType } from 'types/analytics'

import type { GetV2PoolTransactionsQuery } from '../../queries/analytics/v2/generated'
import { useGetV2PoolTransactionsQuery } from '../../queries/analytics/v2/generated'

type TransactionsResponse = GetV2PoolTransactionsQuery

type MintTransaction = NonNullable<TransactionsResponse['mintV2S']>[number]
type BurnTransaction = NonNullable<TransactionsResponse['burnV2S']>[number]
type SwapTransaction = NonNullable<TransactionsResponse['swapV2S']>[number]

const parseAmount = (value?: string | null) => {
  if (!value) return 0
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toProcessedTransaction = (
  tx: MintTransaction | BurnTransaction | SwapTransaction,
  type: TransactionType,
  sender: string,
  token0Amount: number,
  token1Amount: number,
  amountUSD: number
): Transaction => ({
  type,
  hash: tx.transaction.id,
  timestamp: tx.timestamp,
  sender,
  token0: {
    symbol: tx.pair.token0.symbol,
    address: tx.pair.token0.id,
    amount: token0Amount,
  },
  token1: {
    symbol: tx.pair.token1.symbol,
    address: tx.pair.token1.id,
    amount: token1Amount,
  },
  amountUSD,
})

const processV2PoolTransactions = (txs: TransactionsResponse): Transaction[] => {
  const mints = (txs.mintV2S ?? []).map((tx) =>
    toProcessedTransaction(
      tx,
      TransactionType.MINT,
      tx.to ?? '',
      parseAmount(tx.amount0),
      parseAmount(tx.amount1),
      parseAmount(tx.amountUSD)
    )
  )

  const burns = (txs.burnV2S ?? []).map((tx) =>
    toProcessedTransaction(
      tx,
      TransactionType.BURN,
      tx.sender ?? '',
      parseAmount(tx.amount0),
      parseAmount(tx.amount1),
      parseAmount(tx.amountUSD)
    )
  )

  const swaps = (txs.swapV2S ?? []).map((tx) =>
    toProcessedTransaction(
      tx,
      TransactionType.SWAP,
      tx.to ?? '',
      parseAmount(tx.amount0In) - parseAmount(tx.amount0Out),
      parseAmount(tx.amount1In) - parseAmount(tx.amount1Out),
      parseAmount(tx.amountUSD)
    )
  )

  return [...mints, ...burns, ...swaps].sort((a, b) => Number(b.timestamp) - Number(a.timestamp))
}

type UseV2PoolTransactionsOptions = {
  enabled?: boolean
}

export const useV2PoolTransactions = (poolAddress: string, options?: UseV2PoolTransactionsOptions) => {
  const subgraphConfig = useSubgraphConfig(SubgraphType.V2)
  const endpoint = subgraphConfig.endpoint
  const normalizedAddress = poolAddress.toLowerCase()

  const {
    data = [],
    isLoading,
    error,
  } = useGetV2PoolTransactionsQuery(
    {
      endpoint,
    },
    {
      address: normalizedAddress,
    },
    {
      ...options,
      select: (data) => processV2PoolTransactions(data),
    }
  )

  return {
    data,
    isLoading,
    error,
  }
}
